import { useNavigate } from "react-router-dom";
import { Play, Pause, SkipBack, SkipForward, Volume2, Lock, Maximize2 } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/api";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

export default function PlayerBar() {
  const { current, isPlaying, progress, duration, togglePlay, seek, playNext, playPrev, volume, setVolume, previewLimit } = usePlayer();
  const { isPremium } = useAuth();
  const nav = useNavigate();

  if (!current) return null;

  const cover = fileUrl(current.cover_file_id);
  const max = duration || 100;
  const previewPct = isPremium ? 100 : Math.min(100, (previewLimit / max) * 100);

  return (
    <div data-testid="player-bar" className="fixed bottom-0 inset-x-0 z-50 bg-[#0a0a0a] border-t border-border md:pl-64">
      <div className="flex items-center gap-4 px-4 py-3">
        {/* track info */}
        <div className="flex items-center gap-3 w-1/3 min-w-0">
          <button data-testid="player-open-track" onClick={() => nav(`/track/${current.id}`)} title="Expand to track page" className="flex items-center gap-3 min-w-0 text-left group/expand">
            <div className="w-12 h-12 bg-secondary border border-border shrink-0 overflow-hidden relative">
              {cover ? <img src={cover} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-primary/20" />}
              <span className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover/expand:opacity-100 transition-opacity">
                <Maximize2 size={16} className="text-white" />
              </span>
            </div>
            <div className="min-w-0">
              <div data-testid="player-title" className="font-display font-semibold text-sm truncate group-hover/expand:text-primary transition-colors">{current.title}</div>
              <div className="text-xs text-muted-foreground truncate">{current.artist}</div>
            </div>
          </button>
        </div>

        {/* controls */}
        <div className="flex-1 flex flex-col items-center gap-1">
          <div className="flex items-center gap-5">
            <button data-testid="player-prev" onClick={playPrev} className="text-muted-foreground hover:text-white transition-colors"><SkipBack size={18} /></button>
            <button data-testid="player-toggle" onClick={togglePlay} className="w-10 h-10 bg-primary hover:bg-white hover:text-primary flex items-center justify-center transition-colors">
              {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
            </button>
            <button data-testid="player-next" onClick={playNext} className="text-muted-foreground hover:text-white transition-colors"><SkipForward size={18} /></button>
          </div>
          <div className="w-full flex items-center gap-2 max-w-md">
            <span className="text-[10px] text-muted-foreground tabular-nums">{fmt(progress)}</span>
            <div className="relative flex-1 h-1.5 bg-secondary cursor-pointer group" onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              seek(((e.clientX - rect.left) / rect.width) * max);
            }}>
              {!isPremium && (
                <div className="absolute top-0 h-full bg-white/5 border-r border-accent" style={{ left: 0, width: `${previewPct}%` }} />
              )}
              <div className="absolute top-0 left-0 h-full bg-primary" style={{ width: `${Math.min(100, (progress / max) * 100)}%` }} />
            </div>
            <span className="text-[10px] text-muted-foreground tabular-nums">{fmt(duration)}</span>
          </div>
          {!isPremium && (
            <button data-testid="player-preview-badge" onClick={() => nav("/subscribe")} className="text-[10px] overline text-accent flex items-center gap-1 hover:text-primary transition-colors">
              <Lock size={10} /> 25s Preview · Unlock full
            </button>
          )}
        </div>

        {/* volume */}
        <div className="hidden md:flex items-center gap-2 w-1/4 justify-end">
          <Volume2 size={16} className="text-muted-foreground" />
          <input data-testid="player-volume" type="range" min="0" max="1" step="0.05" value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-24 accent-primary" />
        </div>
      </div>
    </div>
  );
}
